import { DATA_CAPABILITY_ICONS } from '../data/dataCapabilities';
import { TASK_FAMILY_LABELS, WEB_PLATFORM_LABEL } from '../data/platformLabels';

const PLATFORM_NAMES: Record<string, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  perplexity: 'Perplexity',
  rednote: 'RedNote (Xiaohongshu)',
  twitter: 'X / Twitter',
  hackernews: 'Hacker News',
  truthsocial: 'Truth Social',
  appstore: 'App Store',
  playstore: 'Google Play',
  article: 'Articles',
  maps: 'Google Maps',
  email: 'Email Tools',
  leads: 'B2B Leads',
  person: 'People Data',
  social: 'Social Profiles',
  realtor: 'Realtor.com',
  booking: 'Booking.com',
  coinmarketcap: 'CoinMarketCap',
  congress: 'Congress Trades',
  sec: 'SEC EDGAR',
  duckduckgo: 'DuckDuckGo',
  levels: 'Levels.fyi',
  bestbuy: 'Best Buy',
  samsclub: "Sam's Club",
  asos: 'ASOS',
  ikea: 'IKEA',
  aliexpress: 'AliExpress',
  web: WEB_PLATFORM_LABEL,
  ...TASK_FAMILY_LABELS,
};

const PLATFORM_WORDS: Record<string, string> = {
  tiktok: 'TikTok',
  youtube: 'YouTube',
  linkedin: 'LinkedIn',
  github: 'GitHub',
  ebay: 'eBay',
  seo: 'SEO',
};

const ICON_GROUPS = [
  ['message-circle', 'reddit weibo zhihu truthsocial snapchat'],
  ['music-2', 'tiktok douyin'],
  ['book-open', 'rednote'],
  ['instagram', 'instagram'],
  ['youtube', 'youtube'],
  ['at-sign', 'twitter threads email social'],
  ['facebook', 'facebook'],
  ['linkedin', 'linkedin'],
  ['search', 'google google_ads person chatgpt gemini perplexity bing brave duckduckgo'],
  ['github', 'github'],
  ['globe', 'web'],
  ['newspaper', 'substack hackernews article'],
  ['star', 'pinterest trustpilot g2 capterra'],
  ['music', 'spotify'],
  ['shopping-cart', 'amazon aliexpress alibaba google_shopping walmart target bestbuy samsclub ikea'],
  ['shopping-bag', 'ebay etsy tiktok_shop asos shopify'],
  ['tag', 'fiverr indeed glassdoor upwork levels slickdeals'],
  ['package', 'appstore'],
  ['play', 'playstore'],
  ['bar-chart-3', 'seo ahrefs semrush similarweb'],
  ['map', 'maps'],
  ['map-pin', 'yelp zillow realtor redfin nextdoor'],
  ['database', 'leads apollo crunchbase'],
  ['bed-double', 'booking airbnb google_hotels vrbo'],
  ['plane', 'tripadvisor google_flights'],
  ['trending-up', 'yahoo_finance google_finance polymarket'],
  ['bitcoin', 'coinmarketcap'],
  ['landmark', 'congress sec'],
] as const;

const PLATFORM_IDS: Readonly<Record<string, string>> = {
  twitter: 'x-twitter',
};

const PLATFORM_KEYS = Object.fromEntries(
  Object.entries(PLATFORM_IDS).map(([key, id]) => [id, key]),
);
const PLATFORM_ICON_ENTRIES = ICON_GROUPS.flatMap(([icon, keys]) =>
  keys.split(' ').map((key) => [key, icon] as const),
);
const PLATFORM_ICONS = new Map<string, string>([
  ...PLATFORM_ICON_ENTRIES,
  ...Object.entries(DATA_CAPABILITY_ICONS),
]);

export const titleCase = (value: string): string =>
  PLATFORM_WORDS[value] ??
  (value ? value[0].toUpperCase() + value.slice(1) : value);

const fallbackName = (key: string): string =>
  PLATFORM_NAMES[key] ?? key.split('_').map(titleCase).join(' ');

export const platformKey = (slug: string): string => slug.split('.')[0];

/** Platforms the gateway still sells that the site and dashboard do not show.
 *  Every catalog, search, usage, and benchmark reader drops their SKUs, so their
 *  pages answer not-found and nothing lists, links, or draws them. */
const UNLISTED_PLATFORMS: ReadonlySet<string> = new Set(['apollo']);

/** Whether a SKU may appear on the site and dashboard. */
export const isListedSku = (slug: string): boolean => !UNLISTED_PLATFORMS.has(platformKey(slug));

export const platformKeyForRoute = (routeId: string): string =>
  PLATFORM_KEYS[routeId] ?? routeId.replace(/-/g, '_');

export const platformMeta = (
  key: string,
): { id: string; name: string; icon: string } => ({
  id: PLATFORM_IDS[key] ?? key.replace(/_/g, '-'),
  name: fallbackName(key),
  icon: PLATFORM_ICONS.get(key) ?? 'box',
});

/** The endpoint action a slug names: "tiktok.profile_videos" → "profile videos". */
export const actionOf = (slug: string): string =>
  slug.split('.').slice(1).join(' ').replace(/_/g, ' ').trim() || slug;

export const endpointIdOf = (slug: string): string => actionOf(slug).replace(/ /g, '-');

/** The catalog card id a SKU slug belongs to ("tiktok.profile" -> "tiktok"). */
export const platformIdOf = (slug: string): string => platformMeta(platformKey(slug)).id;
